type NodeId = string;
type NodeType = string;

interface ScenarioNode {
  id: NodeId;
  type?: NodeType;
  branchId?: string;
  parentBranchId?: string;
  parentNodeId?: string;
  data?: {
    nodeType: NodeType;
  }
  nextNodes?: NodeId[];
  prevNodes?: NodeId[];
}

type Payload = Record<NodeId, ScenarioNode>;

type Change =
  | { kind: "added"; nodeId: NodeId; nodeType: NodeType; message: string }
  | { kind: "deleted"; nodeId: NodeId; nodeType: NodeType; message: string }
  | { kind: "moved"; nodeId: NodeId; nodeType: NodeType; details: string[]; message: string }
  | { kind: "edited"; nodeId: NodeId; nodeType: NodeType; fields: string[]; message: string; previousValue?: string; newValue?: string };

// --- Utility helpers ---
const asSetString = (a?: string[]) =>
  JSON.stringify([...(a ?? [])].sort());

const getNestedValue = (obj: unknown, path: string[]): unknown => {
  return path.reduce((acc: unknown, curr: string) => {
    return (acc as Record<string, unknown>)?.[curr];
  }, obj);
};

const eq = (a: unknown, b: unknown) =>
  JSON.stringify(a) === JSON.stringify(b);

function diffPrimitiveFields<T extends object>(
  before: T | undefined,
  after: T | undefined
): string[] {
  const beforeKeys = new Set(Object.keys(before ?? {}));
  const afterKeys = new Set(Object.keys(after ?? {}));
  const all = new Set([...beforeKeys, ...afterKeys]);
  const changed: string[] = [];
  for (const k of all) {
    const va = (before as unknown as Record<string, unknown>)?.[k];
    const vb = (after as unknown as Record<string, unknown>)?.[k];
    if (!eq(va, vb)) changed.push(k);
  }
  return changed;
}

const NODE_EDIT_KEYS = ["type"] as const;

function movedDetails(before: ScenarioNode, after: ScenarioNode): string[] {
  const diffs: string[] = [];
  if (asSetString(before.prevNodes) !== asSetString(after.prevNodes)) diffs.push("prevNodes");
  if ((before.parentNodeId ?? null) !== (after.parentNodeId ?? null)) diffs.push("parentNodeId");
  if ((before.branchId ?? null) !== (after.branchId ?? null)) diffs.push("branchId");
  if ((before.parentBranchId ?? null) !== (after.parentBranchId ?? null)) diffs.push("parentBranchId");
  return diffs;
}

function editedFields(before: ScenarioNode, after: ScenarioNode): string[] {
  const nodeLevel: string[] = [];
  for (const k of NODE_EDIT_KEYS) {
    const va = before[k];
    const vb = after[k];
    if (!eq(va, vb)) nodeLevel.push(k);
  }
  const dataChanged = diffPrimitiveFields(before.data, after.data).map((k) => `data.${k}`);
  return [...nodeLevel, ...dataChanged];
}

// --- Core diff ---
export function diffScenario(
  prevPayload: Payload | null | undefined,
  nextPayload: Payload,
): Change[] {
  const before = prevPayload ?? {};
  const after = nextPayload ?? {};
  const changes: Change[] = [];

  const beforeIds = new Set(Object.keys(before));
  const afterIds = new Set(Object.keys(after));

  // Added
  for (const id of afterIds) {
    if (!beforeIds.has(id)) {
      const n = after[id];
      const nodeType = n?.data?.nodeType;
      if (nodeType) {
        changes.push({
          kind: "added",
          nodeId: id,
          nodeType,
          message: `Added “${nodeType}” node (${id})`,
        });
      }
    }
  }

  // Deleted
  for (const id of beforeIds) {
    if (!afterIds.has(id)) {
      const n = before[id];
      const nodeType = n?.data?.nodeType;
      if (nodeType) {
        changes.push({
          kind: "deleted",
          nodeId: id,
          nodeType,
          message: `Deleted “${nodeType}” node (${id})`,
        });
      }
    }
  }

  // Persisting nodes: detect moved and edited
  for (const id of afterIds) {
    if (!beforeIds.has(id)) continue;
    const b = before[id];
    const a = after[id];

    // Moved?
    const move = movedDetails(b, a);
    if (move.length > 0 && a?.data?.nodeType) {
      changes.push({
        kind: "moved",
        nodeId: id,
        nodeType: a?.data?.nodeType,
        details: move,
        message: `Moved “${a.data?.nodeType}” node (${id})`,
      });
    }

    // Edited?
    const edited = editedFields(b, a);
    if (edited.length > 0 && a?.data?.nodeType) {
      // And field-specific lines (as requested)
      let editedCount = 0;
      for (const f of edited) {
        const path = f.split('.');
        const previousValue = JSON.stringify(getNestedValue(b, path));
        const newValue = JSON.stringify(getNestedValue(a, path));
        if (a?.data?.nodeType) {
          editedCount++;
          changes.push({
            kind: "edited",
            nodeId: id,
            nodeType: a?.data?.nodeType,
            fields: [f],
            previousValue,
            newValue,
            message: `Updated “${f}” for “${a?.data?.nodeType}” node (${id})`,
          });
        }
      }
      if (editedCount === 0) {
        changes.push({
          kind: "edited",
          nodeId: id,
          nodeType: a?.data?.nodeType,
          fields: edited,
          message: `Updated “${a?.data?.nodeType}” node (${id})`,
        });
      }
    }
  }

  return changes;
}

// --- Merge log formatters ---

const mergeAddedAndDeleted = (logs: Change[]) => {
  const foundAdded = logs.find(item => item?.kind === 'added');
  const foundDeleted = logs.find(item => item?.kind === 'deleted');
  const isTheSameNodeType = foundAdded?.nodeType === foundDeleted?.nodeType;

  // replace deleted and added with only one item 'moved'
  if (foundAdded && foundDeleted && isTheSameNodeType) {
    logs = logs.filter(item => item?.kind !== 'deleted' && item?.kind !== 'added');
    logs.push({
      kind: 'moved',
      nodeId: foundAdded?.nodeId,
      nodeType: foundAdded?.nodeType,
      details: [],
      message: `Moved “${foundAdded?.nodeType}” node (${foundDeleted?.nodeId} => ${foundAdded?.nodeId})`,
    });
  }

  return logs;
}

const mergeAddedAndMoved = (logs: Change[]) => {
  const foundAdded = logs.find(item => item?.kind === 'added');
  const foundMoved = logs.find(item => item?.kind === 'moved');
  const foundDeleted = logs.find(item => item?.kind === 'deleted');

  if (foundAdded && foundMoved && !foundDeleted) {
    logs = logs.filter(item => item?.kind !== 'moved');
  }

  return logs;
}

const mergeBehaviorCondition = (operation = 'added', type = 'if:condition-behavior') => (logs: Change[]) => {
  const foundAdded = logs.find(item => item?.kind === operation && item?.nodeType === type);

  if (foundAdded) {
    logs = logs.filter(item => item?.kind === operation && !['if:condition-close', 'if:branch'].includes(item?.nodeType));
  }

  return logs;
}

const mergeDeletedAndMoved = (logs: Change[]) => {
  const foundDeleted = logs.find(item => item?.kind === 'deleted');
  const foundMoved = logs.find(item => item?.kind === 'moved');

  if (foundDeleted && foundMoved) {
    logs = logs.filter(item => item?.kind !== 'moved');
  }

  return logs;
}

const mergeMovedAndMoved = (logs: Change[]) => {
  const foundMoved = logs.find(item => item?.kind === 'moved' && item?.message.includes('=>'));
  const foundMoved2 = logs.find(item => item?.kind === 'moved' && !item?.message.includes('=>'));

  if (foundMoved && foundMoved2) {
    logs = logs.filter(item => item?.kind === 'moved' && item?.message.includes('=>'));
  }

  return logs;
}

const trimSettledInputValues = (logs: Change[]) => {
  logs = logs.filter(item => item?.kind !== 'edited' || (item?.kind === 'edited' && item.previousValue !== undefined && item.newValue !== ""));

  return logs;
}

export const merges = [
  mergeAddedAndDeleted,
  mergeAddedAndMoved,
  mergeBehaviorCondition('added', 'if:condition-behavior'),
  mergeBehaviorCondition('deleted', 'if:condition-behavior'),
  mergeBehaviorCondition('added', 'if:condition'),
  mergeBehaviorCondition('deleted', 'if:condition'),
  mergeDeletedAndMoved,
  mergeMovedAndMoved,
  trimSettledInputValues,
]

let previousPayload: Payload | null = null;

export function onChange(nextPayload: Payload, extraMerges: ((logs: Change[]) => Change[])[] = []) {
  let logs = diffScenario(previousPayload, nextPayload);

  console.log(`>>>>>> logs before trim`, JSON.stringify(logs, null, 2));
  merges.concat(extraMerges).forEach(fn => logs = fn(logs));
  console.log('>>>>>>> logs after trim', JSON.stringify(logs, null, 2));

  // Pipe logs to your tracker. Example: console + return.
  for (const l of logs) console.log(l.message);

  // Update baseline for next change
  previousPayload = nextPayload;

  return logs;
}

export function setPreviousPayload(payload: Payload) {
  previousPayload = payload;
}

export function comparePayload(nextPayload: Payload, referencePayload: Payload) {
  return diffScenario(referencePayload, nextPayload);
}
